import React from 'react';

import {
  openCreateAlignmentsModalWithIds,
  openCreateAlignmentsModalWithSequences,
} from 'analyses/alignment/modals/SharedUtil';
import {track} from 'analytics/Analytics';
import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {CreateAssemblyModal} from 'assembly/CreateAssemblyModal';
import BulkCreateInventoryFilesFlow from 'bulk-import/BulkCreateInventoryFilesFlow';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import * as FolderConstants from 'constants/FolderConstants';
import CreateSourceModal from 'create-components/CreateSourceModal';
import CreateFileModal from 'files/CreateFileModal';
import CreateEntryModal from 'folder-items/CreateEntryModal';
// @ts-expect-error
import CreateProtocolModal from 'folder-items/CreateProtocolModal';
import CreateFolderModal from 'folders/CreateFolderModal';
import ImportSequenceModal from 'import-sequence/ImportSequenceModal';
import {openCRISPRModal} from 'molbio/crispr/CRISPRModal';
import ImportSequenceForHRTemplateModal from 'molbio/crispr/hr-template/ImportSequenceForHRTemplateModal';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import CreateOligoModal from 'molbio/oligos/CreateOligoModal';
import {openBatchSequenceImportModal, openImportOligoModal} from 'nav/CreateDropdownActions';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import CreateTemplateModal from 'notebook/entry-templates/CreateTemplateModal';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import {MODAL_CONTAINER_KEY} from 'notebook/template-collections/TemplateCollectionUtil';
import SidebarActions from 'sidebar/SidebarActions';
import * as ModalContainer from 'ui-library/ModalContainer';
import runModal from 'ui-library/runModal';

export enum CreateModal {
  ALIGNMENT = 'ALIGNMENT',
  ASSEMBLY = 'ASSEMBLY',
  BATCH_SEQUENCE_IMPORT = 'BATCH_SEQUENCE_IMPORT',
  CRISPR = 'CRISPR',
  ENTITIES_FROM_SCHEMAS = 'ENTITIES_FROM_SCHEMAS',
  ENTRY = 'ENTRY',
  FILE = 'FILE',
  FOLDER = 'FOLDER',
  HR_TEMPLATE = 'HR_TEMPLATE',
  OLIGO = 'OLIGO',
  OLIGO_IMPORT = 'OLIGO_IMPORT',
  PROJECT = 'PROJECT',
  PROTOCOL = 'PROTOCOL',
  SEQUENCE = 'SEQUENCE',
  SEQUENCE_IMPORT = 'SEQUENCE_IMPORT',
  TEMPLATE = 'TEMPLATE',
}

export interface CreateModalDatum {
  anchor: string;
  callback: () => void;
  hashCreateCallbackOverride?: (hash: string) => void;
}

export const CREATE_MODAL_DATA: Record<CreateModal, CreateModalDatum> = {
  [CreateModal.ALIGNMENT]: {
    anchor: '#create/alignment',
    callback: () => {
      track('alignment', 'openModal', {source: 'global-create'});
      openCreateAlignmentsModalWithSequences([]);
    },
    hashCreateCallbackOverride: (hash) => {
      if (hash.indexOf('#create/alignment?') === 0) {
        const sequenceIds = hash.slice('#create/alignment?'.length).split('&');
        openCreateAlignmentsModalWithIds(sequenceIds);
      } else {
        openCreateAlignmentsModalWithSequences([]);
      }
    },
  },
  [CreateModal.ASSEMBLY]: {
    anchor: '#create/assembly',
    callback: () => {
      runModal(CreateAssemblyModal, {});
    },
  },
  [CreateModal.BATCH_SEQUENCE_IMPORT]: {
    anchor: '#batch-sequence-import',
    callback: () => {
      openBatchSequenceImportModal();
      analytics.track('sequence', 'open_create_sequence_modal', {
        product: ANALYTICS_MODULE.MOLBIO,
        createdFrom: 'global',
        importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      });
    },
    hashCreateCallbackOverride: () => {
      openBatchSequenceImportModal();
      analytics.track('sequence', 'open_create_sequence_modal', {
        product: ANALYTICS_MODULE.MOLBIO,
        createdFrom: 'hash',
        importFlow: DNA_SEQUENCE_IMPORT_FLOW.CHROMOSOME,
      });
    },
  },
  [CreateModal.CRISPR]: {
    anchor: '#create/crispr',
    callback: () => {
      openCRISPRModal({});
    },
    hashCreateCallbackOverride: () => {
      openCRISPRModal({});
      SidebarActions.close();
    },
  },
  [CreateModal.ENTITIES_FROM_SCHEMAS]: {
    anchor: '#import/inventory-files',
    callback: () => {
      BulkCreateInventoryFilesFlow.open({});
    },
  },
  [CreateModal.ENTRY]: {
    anchor: '#create/entry',
    callback: () => {
      // @ts-expect-error
      CreateEntryModal.open(CreateEntryModal, {
        fulfillments: [],
      });
    },
  },
  [CreateModal.FILE]: {
    anchor: '#create/file',
    callback: () => {
      runModal(CreateFileModal, {});
    },
  },
  [CreateModal.FOLDER]: {
    anchor: '#create/folder',
    callback: () => {
      runModal(CreateFolderModal, {
        folderType: FolderConstants.Type.INVENTORY,
        successCallback: (newFolder) => {
          listFolderSection(newFolder, FileBrowserConstants.INVENTORY_SECTION);
        },
      });
    },
  },
  [CreateModal.HR_TEMPLATE]: {
    anchor: '#create/hr-template',
    callback: () => {
      runModal(ImportSequenceForHRTemplateModal, {});
      analytics.track('hr_template', 'open_import_modal_from_create');
    },
    hashCreateCallbackOverride: () => {
      runModal(ImportSequenceForHRTemplateModal, {});
      analytics.track('hr_template', 'open_import_modal_from_hash');
    },
  },
  [CreateModal.OLIGO]: {
    anchor: '#create/oligo',
    callback: () => {
      runModal(CreateOligoModal, {});
    },
  },
  [CreateModal.OLIGO_IMPORT]: {
    anchor: '#import-oligo',
    callback: () => {
      openImportOligoModal(null);
    },
  },
  [CreateModal.PROJECT]: {
    anchor: '#create/project',
    callback: () => {
      runModal(CreateSourceModal, {});
    },
  },
  [CreateModal.PROTOCOL]: {
    anchor: '#create/protocol',
    callback: () => {
      CreateProtocolModal.open(CreateProtocolModal, {});
    },
  },
  [CreateModal.SEQUENCE]: {
    anchor: '#create/sequence',
    callback: () => {
      runModal(ImportSequenceModal, {initialTab: DNA_SEQUENCE_IMPORT_FLOW.RAW});
    },
  },
  [CreateModal.SEQUENCE_IMPORT]: {
    anchor: '#import-sequence',
    callback: () => {
      runModal(ImportSequenceModal, {});
    },
    hashCreateCallbackOverride: (hash) => {
      runModal(ImportSequenceModal, {
        initialTab:
          hash != null && hash.indexOf('dbimport') !== -1
            ? DNA_SEQUENCE_IMPORT_FLOW.DB
            : DNA_SEQUENCE_IMPORT_FLOW.FILE,
      });
    },
  },
  [CreateModal.TEMPLATE]: {
    anchor: '#create/template',
    callback: () => {
      ModalContainer.open(
        MODAL_CONTAINER_KEY,
        <CreateTemplateModal
          onRequestClose={() => {
            $('#fileLoading').addClass('hidden');
            ModalContainer.close(MODAL_CONTAINER_KEY);
          }}
          onSubmit={(name, templateCollection, isProtected, tagSchema, schemaTags) => {
            TemplateUtil.createThenOpen(
              name,
              templateCollection.id,
              isProtected,
              tagSchema ?? undefined,
              schemaTags
            ).done(() => {
              $('#fileLoading').addClass('hidden');
              ModalContainer.close(MODAL_CONTAINER_KEY);
            });
          }}
        />
      );
    },
  },
};
